import { IconButton, useEventCallback } from '@mui/material';
import { headCellsDocumentDeath } from 'civil-status/constants/head-cells';
import { useFormik } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import Table from 'shell/components/shared/table';
import { noop } from 'shell/utils/noop';
import { DocumentListDeathCrumbs } from 'civil-status/constants/breadcrumb';
import {
  DocumentDeathFilterForm,
  DocumentDeathVM,
} from 'civil-status/view-models/document-death-list';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { Delete, Visibility } from '@mui/icons-material';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { DocumentDeathList } from 'shell/models/document-death';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import PmiTooltipError from 'shell/components/shared/tooltip-error';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import { transformRequestBirthCitizenshipDeath } from 'civil-status/utils/transform-request-for-delete-document';
import ListHeader from 'shell/components/shared/list-header';

export default function DocumentDeathListPage(): React.ReactElement {
  const { userProfile } = useUserProfile();
  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<DocumentDeathVM>>({
    element: null,
    open: false,
  });
  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<DocumentDeathVM>>({
    element: null,
    open: false,
  });

  const [deathList, getDeathList] = useHttpFetch<
    HttpResponseList<DocumentDeathList>,
    Array<DocumentDeathVM>
  >(`${__CIVIL_STATUS_API_URL__}/atti-morte-sima`, {
    onBootstrap: true,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
    },
    transformer: (response) => {
      return response.data?.payload?.list.map((item) => {
        const itemResult: DocumentDeathVM = {
          code: item.matricola?.toString() || '',
          surname: item.cognome || '',
          name: item.nome || '',
          year: item.key?.anno || '',
          documentNumber: item.key?.numeroAtto?.toString() || '',
          numReg: item.key?.numeroRegistro || '',
          parte: item.key?.parte || '',
          serie: item.key?.serie1 || '',
          user: item.userLock || '',
          locked: !item.userLock
            ? false
            : item.userLock !== userProfile.datiLogin?.key?.usernameEsteso,
          key: item.key,
        };
        return itemResult;
      });
    },
  });

  const lockDocument = useHttpClient(`${__CIVIL_STATUS_API_URL__}/atti-morte-sima/blocca-atto`, {
    options: {
      errorCatching: true,
      method: 'PUT',
      loading: true,
    },
  });

  const updateFlagDeathDocument = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-morte-sima/aggiornamento-indicatore-caricamento`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
      },
    }
  );

  const data = deathList.data as Array<DocumentDeathVM>;

  const formikForm = useFormik<DocumentDeathFilterForm>({
    initialValues: {
      page: 0,
      rowsPerPage: 10,
      orders: {},
    },
    onSubmit: noop,
  });

  // HTTP Request to get list
  const navigate = useNavigate();

  const onChangePage = useCallback(
    (page) => {
      formikForm.setFieldValue('page', page || 0);
    },
    [formikForm.setFieldValue]
  );

  const onChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...formikForm.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };

      formikForm.setValues(newDataset);
    },
    [formikForm.setValues, formikForm.values]
  );

  const onVisibilityPress = useCallback(
    (cell): Promise<DocumentDeathVM> => {
      return new Promise((resolve) => {
        (
          lockDocument({
            data: {
              key: cell.key,
            },
          }) as ResponsePromise<void>
        ).then(() => {
          navigate({
            pathname: generatePath(
              '/stato-civile/morte/carica/:anno/:numeroRegistro/:numeroAtto/:parte/:serie1/:serie2',
              {
                ...(cell.key || {}),
                parte: cell.key.parte || 'null',
                serie1: cell.key.serie1 || 'null',
                serie2: cell.key.serie2 || 'null',
              }
            ),
          });
          resolve(cell);
        });
      });
    },
    [navigate, lockDocument]
  );

  const deleteDeathDocument = useEventCallback((deathDocument: DocumentDeathVM) => {
    setDeleteAlertModal({ element: deathDocument, open: true });
  });
  /**
   * The list of document act.
   */
  const list = useMemo(() => {
    return (
      data?.map((item) => ({
        ...item,
        action: (
          <>
            <PmiTooltipError
              show={item.locked}
              message={item.locked ? `Atto bloccato dall'utente ${item.user}` : ''}
            >
              <span>
                <IconButton
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onVisibilityPress(item);
                  }}
                  disabled={item.locked}
                >
                  <Visibility />
                </IconButton>
              </span>
            </PmiTooltipError>
            <span>
              <IconButton
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  deleteDeathDocument(item);
                }}
                color="cdmBrand"
              >
                <Delete />
              </IconButton>
            </span>
          </>
        ),
      })) || []
    );
  }, [data, onVisibilityPress, deleteDeathDocument]);

  return (
    <>
      <Breadcrumbs items={DocumentListDeathCrumbs} base="stato-civile" />
      <ListHeader
        addBtnAriaLabel="Aggiungi"
        addBtnLabel="Aggiungi"
        canAdd
        onAddBtnClick={() => {
          navigate('/stato-civile/morte/carica/nuovo');
        }}
        title="Lista Atti Morte - Caricamento"
      />
      <Table
        ariaLabel="Lista atti morte"
        page={formikForm.values.page}
        rowsPerPage={formikForm.values.rowsPerPage}
        body={list}
        count={list.length}
        head={headCellsDocumentDeath}
        onSort={noop}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
        onVisibilityPress={onVisibilityPress}
        onEditPress={noop}
        onTrashPress={noop}
        orders={formikForm.values.orders}
        isNotLayoutFixed
        noSort
        hideButtons
      />
      <DeleteAlertModal
        title="Cancellazione Atto/Morte"
        message="Sei sicuro di considerare l'Atto caricato ed eliminarlo dalla lista?"
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          const requestData = transformRequestBirthCitizenshipDeath(deleteAlertModal.element);
          (
            updateFlagDeathDocument({
              data: requestData,
            }) as ResponsePromise<void>
          ).then(() => {
            setDeleteAlertModal({ open: false, element: null });
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Atto/Morte"
        message={`La cancellazione dell'Atto Morte è avvenuta con successo.`}
        onClose={() => {
          getDeathList();
          setDeleteSuccessModal({ open: false, element: null });
        }}
        onOk={() => {
          getDeathList();
          setDeleteSuccessModal({ open: false, element: null });
        }}
      />
    </>
  );
}

DocumentDeathListPage.displayName = 'home page component';
